import React, { useMemo } from 'react'
import { DcChart, DcDiv, DcFlexBox, DcText, colors } from 'pixel-base'
import { InventoryProduct } from './types'
import { InventoryStats } from './useInventoryStats'

interface LeastCountChartProps {
  data: InventoryProduct[]
  loading: boolean
  error: string | null
  onProductClick?: (product: InventoryProduct) => void
  showGaugeOnly?: boolean
  showCardsOnly?: boolean
  stats?: InventoryStats
  onTitleClick?: () => void
}

const LeastCountChart: React.FC<LeastCountChartProps> = ({
  data,
  loading,
  error,
  onProductClick,
  showGaugeOnly = false,
  showCardsOnly = false,
  stats,
  onTitleClick,
}) => {
  // Transform data for alert cards
  const alertCards = useMemo(() => {
    if (!data || data.length === 0) {
      return []
    }

    return data.slice(0, 9).map((product) => {
      let alertType: 'critical' | 'warning' | 'normal' = 'normal'
      let alertColor = colors.success

      const stock = product.total_current_stock || 0
      if (stock < 5) {
        alertType = 'critical'
        alertColor = colors.danger
      } else if (stock < 10) {
        alertType = 'warning'
        alertColor = colors.warning
      }

      return {
        ...product,
        alertType,
        alertColor,
        current_stock: stock, // For backward compatibility
      }
    })
  }, [data])

  // Use stats data for gauge or calculate from data as fallback
  const stockHealth = useMemo(() => {
    // Use stats data if available
    if (stats?.overall_stock_health) {
      return {
        value: stats.overall_stock_health.score,
        status: stats.overall_stock_health.status,
        criticalCount: stats.overall_stock_health.metrics.out_of_stock,
        warningCount: stats.overall_stock_health.metrics.low_stock,
        normalCount: stats.overall_stock_health.metrics.high_stock,
      }
    }

    // Fallback to calculating from data
    if (!data || data.length === 0) {
      return {
        value: 0,
        status: 'unknown',
        criticalCount: 0,
        warningCount: 0,
        normalCount: 0,
      }
    }

    const criticalCount = data.filter(
      (p) => (p.total_current_stock || 0) < 5,
    ).length
    const warningCount = data.filter(
      (p) =>
        (p.total_current_stock || 0) >= 5 && (p.total_current_stock || 0) < 10,
    ).length
    const normalCount = data.filter(
      (p) => (p.total_current_stock || 0) >= 10,
    ).length

    const totalProducts = data.length
    const healthScore =
      (normalCount * 100 + warningCount * 50 + criticalCount * 0) /
      totalProducts

    let status = 'excellent'
    if (healthScore < 30) status = 'critical'
    else if (healthScore < 60) status = 'warning'
    else if (healthScore < 80) status = 'good'

    return {
      value: Math.round(healthScore),
      status,
      criticalCount,
      warningCount,
      normalCount,
    }
  }, [data, stats])

  // Gauge chart data
  const gaugeData = useMemo(() => {
    const color =
      stockHealth.status === 'critical'
        ? colors.danger
        : stockHealth.status === 'warning'
        ? colors.warning
        : stockHealth.status === 'good'
        ? colors.info
        : colors.success

    return {
      series: [
        {
          name: 'Stock Health',
          type: 'gauge',
          min: 0,
          max: 100,
          splitNumber: 4,
          radius: '80%',
          axisLine: {
            lineStyle: {
              width: 8,
              color: [
                [0.3, colors.danger],
                [0.6, colors.warning],
                [0.8, colors.info],
                [1, colors.success],
              ],
            },
          },
          pointer: {
            itemStyle: {
              color: color,
            },
          },
          axisTick: {
            distance: -8,
            length: 8,
            lineStyle: {
              color: '#fff',
              width: 2,
            },
          },
          splitLine: {
            distance: -8,
            length: 8,
            lineStyle: {
              color: '#fff',
              width: 4,
            },
          },
          axisLabel: {
            color: '#fff',
            distance: -20,
            fontSize: 12,
          },
          detail: {
            valueAnimation: true,
            formatter: '{value}%',
            color: color,
            fontSize: 20,
            fontWeight: 'bold',
          },
          data: [
            {
              value: stockHealth.value,
              name: 'Stock Health',
            },
          ],
        },
      ],
    }
  }, [stockHealth])

  const gaugeOptions = useMemo(
    () => ({
      tooltip: {
        formatter: (params: any) => `
          <div style="padding: 8px;">
            <strong>Overall Stock Health</strong><br/>
            Score: ${params.value}%<br/>
            Status: ${stockHealth.status.toUpperCase()}<br/>
            Critical: ${stockHealth.criticalCount}<br/>
            Warning: ${stockHealth.warningCount}<br/>
            Normal: ${stockHealth.normalCount}
          </div>
        `,
      },
      series: gaugeData.series,
    }),
    [gaugeData, stockHealth],
  )

  if (loading) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText>Loading inventory data...</DcText>
      </DcDiv>
    )
  }

  if (error) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText color={colors.danger}>Error loading data: {error}</DcText>
      </DcDiv>
    )
  }

  // Show only gauge
  if (showGaugeOnly) {
    return (
      <DcDiv>
        <DcChart
          type="gauge"
          data={gaugeData}
          options={gaugeOptions}
          height={300}
          width="100%"
          title="Overall Stock Health"
          isLoading={loading}
          isTitleClickable={!!onTitleClick}
          onClickTitle={onTitleClick}
        />

        {/* Additional Metrics */}
        {stats?.overall_stock_health && (
          <DcDiv
            style={{
              marginTop: '20px',
              padding: '16px',
              backgroundColor: colors.background,
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
            }}
          >
            <DcText
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: colors.text,
                marginBottom: '12px',
              }}
            >
              Stock Health Metrics
            </DcText>

            <DcDiv
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
                fontSize: '12px',
              }}
            >
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Total Products
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.text }}>
                  {stats.overall_stock_health.metrics.total_products.toLocaleString()}
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Total Stock
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.text }}>
                  {stats.overall_stock_health.metrics.total_stock.toLocaleString()}
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Inventory Value
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.text }}>
                  ₨
                  {stats.overall_stock_health.metrics.total_inventory_value.toLocaleString()}
                </DcText>
              </DcDiv>
              <DcDiv>
                <DcText style={{ color: colors.grayMedium }}>
                  Out of Stock
                </DcText>
                <DcText style={{ fontWeight: '600', color: colors.danger }}>
                  {stats.overall_stock_health.metrics.out_of_stock}
                </DcText>
              </DcDiv>
            </DcDiv>
          </DcDiv>
        )}
      </DcDiv>
    )
  }

  // Show only cards
  if (showCardsOnly) {
    return (
      <DcDiv>
        <DcDiv
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
          }}
        >
          {alertCards.map((product, index) => (
            <DcDiv
              key={product.product_id}
              style={{
                padding: '12px',
                borderRadius: '6px',
                backgroundColor: product.alertColor,
                color: 'white',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                border: `2px solid ${product.alertColor}`,
              }}
              hoverStyle={{
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
              onClick={() => onProductClick?.(product)}
            >
              {/* Product Image */}
              {product.variations?.[0]?.image && (
                <DcDiv
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '4px',
                    marginBottom: '8px',
                    backgroundImage: `url(${product.variations[0].image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '1px solid rgba(255,255,255,0.2)',
                  }}
                />
              )}
              <DcText
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  marginBottom: '8px',
                }}
              >
                {product.current_stock}
              </DcText>
              <DcText
                style={{
                  fontSize: '12px',
                  marginBottom: '4px',
                  opacity: 0.9,
                }}
              >
                {product.product_name.length > 20
                  ? product.product_name.substring(0, 20) + '...'
                  : product.product_name}
              </DcText>
              <DcText
                style={{
                  fontSize: '10px',
                  opacity: 0.8,
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                }}
              >
                {product.alertType}
              </DcText>
            </DcDiv>
          ))}
        </DcDiv>
      </DcDiv>
    )
  }

  // Show both (original layout)
  return (
    <DcDiv>
      <DcFlexBox gap="20px" height="auto">
        {/* Primary Chart - Alert Cards Grid */}
        <DcDiv style={{ width: '70%' }}>
          <DcText
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: colors.text,
            }}
          >
            Low Stock Alert Cards
          </DcText>
          <DcDiv
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
            }}
          >
            {alertCards.map((product, index) => (
              <DcDiv
                key={product.product_id}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  backgroundColor: product.alertColor,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  border: `2px solid ${product.alertColor}`,
                }}
                hoverStyle={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                onClick={() => onProductClick?.(product)}
              >
                {/* Product Image */}
                {product.variations?.[0]?.image && (
                  <DcDiv
                    style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '3px',
                      marginBottom: '6px',
                      backgroundImage: `url(${product.variations[0].image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}
                  />
                )}
                <DcText
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    marginBottom: '4px',
                  }}
                >
                  {product.current_stock}
                </DcText>
                <DcText
                  style={{
                    fontSize: '10px',
                    marginBottom: '2px',
                    opacity: 0.9,
                  }}
                >
                  {product.product_name.length > 15
                    ? product.product_name.substring(0, 15) + '...'
                    : product.product_name}
                </DcText>
                <DcText
                  style={{
                    fontSize: '8px',
                    opacity: 0.8,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                  }}
                >
                  {product.alertType}
                </DcText>
              </DcDiv>
            ))}
          </DcDiv>
        </DcDiv>

        {/* Secondary Chart - Gauge Chart */}
        <DcDiv style={{ width: '30%' }}>
          <DcChart
            type="gauge"
            data={gaugeData}
            options={gaugeOptions}
            height={300}
            width="100%"
            title="Overall Stock Health"
            isLoading={loading}
            isTitleClickable={!!onTitleClick}
            onClickTitle={onTitleClick}
          />
        </DcDiv>
      </DcFlexBox>
    </DcDiv>
  )
}

export default LeastCountChart
